import { NavLink } from 'react-router-dom';
import { 
  LayoutDashboard, 
  MessageSquare, 
  Users, 
  BookOpen, 
  Settings,
  LogOut,
  Shield,
  FileText,
  Terminal,
  Cpu,
  Activity,
  FileCode,
  Sliders,
  Zap,
  CreditCard,
  UserCircle,
  Menu,
  Mail
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useState, useEffect } from 'react';
import { getApiBase } from '../../lib/utils';
import { api } from '../../lib/api';

interface SidebarProps {
  onLogout?: () => void;
  onCollapsedChange?: (collapsed: boolean) => void;
}

export function Sidebar({ onLogout, onCollapsedChange }: SidebarProps) {
  const [branding, setBranding] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return localStorage.getItem('sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    fetch(`${getApiBase()}/global-config/public`)
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) {
          setBranding({
            logo_url: data.logo_url ? `${getApiBase()}${data.logo_url}` : null,
            site_name: data.site_name || 'Totemium',
          });
        }
      })
      .catch(() => {});
    
    // Check if user is admin
    api.get('/fapi/users/me')
      .then(res => setIsAdmin(!!res.data?.is_superuser))
      .catch(() => {});
  }, []);

  const toggleSidebar = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    try {
      localStorage.setItem('sidebar_collapsed', String(newState));
    } catch {}
    onCollapsedChange?.(newState);
  };

  // Notify parent of initial state
  useEffect(() => {
    onCollapsedChange?.(isCollapsed);
  }, []);

  const navigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard },
    { name: 'Connaissances', href: '/knowledges', icon: BookOpen },
    { name: 'Agents', href: '/agents', icon: Users },
    { name: 'Conversations', href: '/conversations', icon: MessageSquare },
    { name: 'Email', href: '/email', icon: Mail, badge: 'Nouveau' },
    { name: 'Workflows', href: '/workflows', icon: Zap, badge: 'Bêta' },
  ];

  const secondaryNavigation = [
    { name: 'Admin', href: '/admin', icon: Shield, adminOnly: true },
    { name: 'Configuration', href: '/config', icon: Settings },
    { name: 'Mon compte', href: '/account', icon: UserCircle },
  ];

  
  return (
    <div className={cn(
      "flex h-full flex-col bg-zinc-950 border-r border-white/5 transition-all duration-300",
      isCollapsed ? "w-20" : "w-72"
    )}>
      {/* Logo Section */}
      <div className="flex h-16 shrink-0 items-center gap-3 px-6 border-b border-white/5">
        {isCollapsed ? (
          <button
            onClick={toggleSidebar}
            className="h-8 w-8 rounded bg-emerald-500/10 hover:bg-emerald-500/20 flex items-center justify-center transition-colors"
            aria-label="Ouvrir le menu"
          >
            <Menu className="h-5 w-5 text-emerald-400" />
          </button>
        ) : (
          <>
            <button
              onClick={toggleSidebar}
              className="h-8 w-8 rounded hover:bg-white/5 flex items-center justify-center transition-colors"
              aria-label="Réduire le menu"
            >
              <Menu className="h-5 w-5 text-zinc-400 hover:text-white" />
            </button>
            {branding?.logo_url ? (
              <img 
                src={branding.logo_url} 
                alt="Logo" 
                className="h-8 w-8 object-contain"
              />
            ) : (
              <div className="h-8 w-8 rounded bg-emerald-500 flex items-center justify-center">
                 <LayoutDashboard className="h-5 w-5 text-white" />
              </div>
            )}
            <span className="font-bold text-lg text-white tracking-tight">
              {branding?.site_name || 'Totemium'}
            </span>
          </>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex flex-1 flex-col px-4 py-6 gap-y-8">
        {/* Main Nav */}
        <div className="flex flex-col gap-y-1">
          {!isCollapsed && (
            <div className="text-xs font-semibold leading-6 text-zinc-500 px-2 mb-2 uppercase tracking-wider">
              Menu
            </div>
          )}
          <ul role="list" className="space-y-1">
            {navigation.map((item) => (
              <li key={item.name}>
                <NavLink
                  to={item.href}
                  className={({ isActive }) =>
                    cn(
                      "group flex gap-x-3 rounded-lg p-2 text-sm font-medium leading-6 transition-all duration-200",
                      isActive
                        ? "bg-emerald-500/10 text-emerald-400 shadow-[inset_3px_0_0_0_#10b981]"
                        : "text-zinc-400 hover:bg-white/5 hover:text-white",
                      isCollapsed && "justify-center"
                    )
                  }
                  title={isCollapsed ? item.name : undefined}
                >
                  <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                  {!isCollapsed && (
                    <>
                      <span className="flex-1">{item.name}</span>
                      {item.badge && (
                        <span className="ml-auto rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-semibold text-amber-400 uppercase tracking-wide">
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>

        {/* Secondary Nav (Settings, etc) */}
        <div className="mt-auto">
          {!isCollapsed && (
            <div className="text-xs font-semibold leading-6 text-zinc-500 px-2 mb-2 uppercase tracking-wider">
              Système
            </div>
          )}
          <ul role="list" className="space-y-1">
            {secondaryNavigation.map((item) => {
              if (item.adminOnly && !isAdmin) return null
              return (
                <li key={item.name}>
                  <NavLink
                    to={item.href}
                    className={({ isActive }) =>
                      cn(
                        "group flex gap-x-3 rounded-lg p-2 text-sm font-medium leading-6 transition-all duration-200",
                        isActive
                          ? item.adminOnly
                            ? "bg-violet-500/10 text-violet-400 shadow-[inset_3px_0_0_0_#8b5cf6]"
                            : "bg-emerald-500/10 text-emerald-400"
                          : item.adminOnly
                            ? "text-zinc-400 hover:bg-violet-500/10 hover:text-violet-400"
                            : "text-zinc-400 hover:bg-white/5 hover:text-white",
                        isCollapsed && "justify-center"
                      )
                    }
                    title={isCollapsed ? item.name : undefined}
                  >
                    <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                    {!isCollapsed && item.name}
                  </NavLink>
                </li>
              )
            })}
            <li>
                <button
                  className={cn(
                    "w-full group flex gap-x-3 rounded-lg p-2 text-sm font-medium leading-6 text-zinc-400 hover:bg-red-500/10 hover:text-red-400 transition-all",
                    isCollapsed && "justify-center"
                  )}
                  onClick={onLogout}
                  title={isCollapsed ? "Déconnexion" : undefined}
                >
                  <LogOut className="h-5 w-5 shrink-0" aria-hidden="true" />
                  {!isCollapsed && "Déconnexion"}
                </button>
            </li>
          </ul>
        </div>
      </nav>
    </div>
  );
}
